import type {
  FindingState,
  Odontogram,
  OdontologyFinding,
  OdontologySpan,
  OdontologyToothStatus,
  ToothSurface as ApiToothSurface,
} from '../../services/OdontologyController';

export const SURFACES = ['vestibular', 'lingual', 'mesial', 'distal', 'occlusal'] as const;
export type ToothSurface = typeof SURFACES[number];
export type FaceState = 'healthy' | 'pending' | 'existing';
export type Dentition = 'permanent' | 'temporary';

export type OdontogramTool =
  | 'caries'
  | 'restoration'
  | 'crown'
  | 'endodontics'
  | 'bridge'
  | 'absent'
  | 'healthy'
  | 'clearTooth';

export interface ToothFaces {
  vestibular: FaceState;
  lingual: FaceState;
  mesial: FaceState;
  distal: FaceState;
  occlusal: FaceState;
}

export interface ToothChart {
  toothId: number;
  faces: ToothFaces;
  absent: boolean;
  crown: FindingState | null;
  endodontics: FindingState | null;
  bridge: FindingState | null;
  mobility: number | null;
  furcation: number | null;
  notes: string;
}

export type OdontogramState = Record<number, ToothChart>;

export const SURFACE_TO_API: Record<ToothSurface, ApiToothSurface> = {
  vestibular: 'V',
  lingual: 'L',
  mesial: 'M',
  distal: 'D',
  occlusal: 'O',
};

export const API_TO_SURFACE: Record<ApiToothSurface, ToothSurface> = {
  V: 'vestibular',
  L: 'lingual',
  M: 'mesial',
  D: 'distal',
  O: 'occlusal',
};

const PERMANENT_QUADRANTS = [1, 2, 3, 4];
const TEMPORARY_QUADRANTS = [5, 6, 7, 8];

const emptyFaces = (): ToothFaces => ({
  vestibular: 'healthy',
  lingual: 'healthy',
  mesial: 'healthy',
  distal: 'healthy',
  occlusal: 'healthy',
});

export const createInitialOdontogram = (): OdontogramState => {
  const chart: OdontogramState = {};
  PERMANENT_QUADRANTS.forEach((quadrant) => {
    for (let position = 1; position <= 8; position += 1) {
      const toothId = quadrant * 10 + position;
      chart[toothId] = createTooth(toothId);
    }
  });
  TEMPORARY_QUADRANTS.forEach((quadrant) => {
    for (let position = 1; position <= 5; position += 1) {
      const toothId = quadrant * 10 + position;
      chart[toothId] = createTooth(toothId);
    }
  });
  return chart;
};

const createTooth = (toothId: number): ToothChart => ({
  toothId,
  faces: emptyFaces(),
  absent: false,
  crown: null,
  endodontics: null,
  bridge: null,
  mobility: null,
  furcation: null,
  notes: '',
});

export const materializeChart = (odontogram: Odontogram): OdontogramState => {
  const chart = createInitialOdontogram();
  odontogram.findings.forEach((finding) => {
    const tooth = chart[finding.toothFDI];
    if (!tooth) return;
    if (finding.typeCode === 'crown') tooth.crown = finding.state;
    if (finding.typeCode === 'endodontics') tooth.endodontics = finding.state;
    if (finding.surface && (finding.typeCode === 'caries' || finding.typeCode === 'restoration')) {
      const surface = API_TO_SURFACE[finding.surface];
      tooth.faces[surface] = finding.state === 1 ? 'pending' : 'existing';
    }
  });
  odontogram.spans.forEach((span) => {
    Object.values(chart).forEach((tooth) => {
      if (isToothInSpan(tooth.toothId, span)) tooth.bridge = span.state;
    });
  });
  odontogram.teeth.forEach((status) => {
    const tooth = chart[status.toothFDI];
    if (!tooth) return;
    tooth.absent = !status.present;
    tooth.mobility = status.mobility ?? null;
    tooth.furcation = status.furcation ?? null;
    tooth.notes = status.notes ?? '';
  });
  return chart;
};

export const isToothInSpan = (toothId: number, span: OdontologySpan): boolean => {
  const arch = [
    [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
    [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
    [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
    [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
  ].find((candidate) => candidate.includes(span.fromToothFDI)
    && candidate.includes(span.toToothFDI));
  if (!arch) return false;
  const start = arch.indexOf(span.fromToothFDI);
  const end = arch.indexOf(span.toToothFDI);
  const tooth = arch.indexOf(toothId);
  return tooth >= Math.min(start, end) && tooth <= Math.max(start, end);
};

export const areTeethInSameArch = (first: number, second: number): boolean =>
  isToothInSpan(first, {
    fromToothFDI: first,
    toToothFDI: second,
    typeCode: 'bridge',
    state: 1,
  });

export const upsertFinding = (
  findings: OdontologyFinding[],
  next: OdontologyFinding,
): OdontologyFinding[] => [
  ...findings.filter((finding) => !(
    finding.toothFDI === next.toothFDI
    && finding.surface === next.surface
    && finding.typeCode === next.typeCode
  )),
  next,
];

export const upsertStatus = (
  statuses: OdontologyToothStatus[],
  next: OdontologyToothStatus,
): OdontologyToothStatus[] => [
  ...statuses.filter((status) => status.toothFDI !== next.toothFDI),
  next,
];

export const normalizeOdontogram = (value: Odontogram): Odontogram => ({
  chartId: value.chartId,
  version: value.version ?? 0,
  dentitionType: value.dentitionType ?? 3,
  savedAt: value.savedAt,
  findings: value.findings ?? [],
  spans: value.spans ?? [],
  teeth: value.teeth ?? [],
});

export const serializeOdontogram = (value: Odontogram): string => JSON.stringify({
  chartId: value.chartId,
  version: value.version,
  dentitionType: value.dentitionType,
  findings: value.findings,
  spans: value.spans,
  teeth: value.teeth,
});
